<template>
   <section class="overflow-hidden">
      <div class="mx-auto flex max-w-2xl flex-col px-2 lg:px-4">
         <!-- In this instance the 'slider' will act as tabs, but the same component can handle this. -->
         <Carousel
            :totalSlides="slides.length"
            :updateCurrentSlideTo="state.updateCurrentSlideTo"
            noSlide
            class="md:order-2"
            name="features"
            @current-slide-change-to="updateSlideFromCarousel"
         >
            <template v-for="(slide, index) in slides" :key="`landing-feature-slide-${index}`" v-slot:[`slot-${index}`]>
               <div class="md-min-h-inherit md:flex">
                  <div class="md-min-h-inherit order-2 md:h-full md:w-1/2 md:pl-4 lg:w-2/3">
                     <div
                        class="mt-2 h-48 w-full overflow-hidden rounded-xl md:mt-0 md:h-full"
                        style="min-height: inherit"
                     >
                        <LazyLoadingBGimage :mainSrc="slide.image.url.md" :srcW768="slide.image.url.sm" />
                     </div>
                  </div>
                  <div
                     class="md-min-h-inherit mt-5 flex flex-col justify-between pr-4 md:mt-0 md:w-1/2 lg:w-1/3 lg:pr-10"
                  >
                     <div class="flex flex-col justify-center pr-6 md:h-full">
                        <h3 class="text-header text-2xl leading-tight text-brand-dark md:text-4xl lg:leading-8">
                           {{ slide.heading }}
                        </h3>
                        <RichTextRenderer :document="slide.description" class="mt-4" />
                     </div>
                     <div v-if="slide.learnMoreLink" class="mt-6">
                        <router-link
                           :to="
                              $route.params?.locale
                                 ? `/${$route.params?.locale}${slide.learnMoreLink}`
                                 : slide.learnMoreLink
                           "
                           class="btn border"
                        >
                           {{ $t("system.learn_more") }}
                        </router-link>
                     </div>
                  </div>
               </div>
            </template>
         </Carousel>

         <!-- Set up a separate set of 'goToSlide controls' for this instance as they are quite specific looking -->
         <!-- Also if we can pass in 'goToSlide' changes this gives us extra flexibility -->
         <ul class="relative mb-4 mt-6 flex items-center justify-center space-x-4 md:mb-12 md:mt-4">
            <li
               v-for="(slide, index) in slides"
               :key="`caro-control-button-${index}`"
               :class="`block relative md:w-1/${slides.length} md:flex-grow`"
            >
               <!-- The current slide / tab -->
               <DefaultButton
                  v-if="state.featuresCurrentSlide === index"
                  :aria-label="$t('redirects.active_slide', { slide: slide.label })"
                  class="!p-2 md:w-full md:!px-8 md:!py-4"
                  @click="slideJumpTo(index)"
               >
                  <span class="hidden md:block">{{ slide.label }}</span>
               </DefaultButton>
               <!-- Other slides / tabs -->
               <DefaultButton
                  v-else
                  :aria-label="$t('redirects.jump_to_slide', { slide: slide.label })"
                  class="!p-2 md:w-full md:!px-8 md:!py-4"
                  styleType="outlined"
                  @click="slideJumpTo(index)"
               >
                  <span class="hidden md:block">{{ slide.label }}</span>
               </DefaultButton>
            </li>
         </ul>
      </div>
   </section>
</template>

<script setup lang="ts">
import type { ImageData } from "@/types";

// declarations
// -----------------------------------------
const props = withDefaults(
   defineProps<{
      type: "carousel_section";
      margin?: string;
      slides: {
         label: string;
         heading: string;
         description: string[];
         image: ImageData;
         learnMoreLink?: string;
      }[];
   }>(),
   {}
);

// state
const state = reactive({
   updateCurrentSlideTo: null,
   featuresCurrentSlide: 0,
});

// methods
// ----------------------------------------
const slideJumpTo = (index) => {
   state.updateCurrentSlideTo = index;
};

const updateSlideFromCarousel = (index) => {
   state.featuresCurrentSlide = index;
};
</script>

<style scoped lang="scss">
@media (min-width: 768px) {
   .md-min-h-inherit {
      min-height: inherit;
   }
}

.image {
   @apply mx-auto;

   @media (max-width: 767px) {
      min-height: 400px;
      max-width: 600px;
   }
}
</style>
